import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as companyGroupService from "../../../models/services/companyGroupService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ICompanyListItem } from "source/models/company/companyListItem";
import { ICompanyGroupEditItem } from "source/models/companyGroup/companyGroupEditItem";
import { CompanyGroupEditDialogCompanyList } from "./CompanyGroupEditDialogCompanyList";

/* eslint-disable no-unused-vars */
export const enum CompanyGroupDialogMenuItem {
    General = "1",
    Companies = "2"
}
/* eslint-enable no-unused-vars */

export interface ICompanyGroupEditDialogProp {
    sessionState: ISessionState;
    defaultMenuItem: CompanyGroupDialogMenuItem;
    companyGroupId: string;
    classes?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: () => void;
    onClose: (companyGroupDataWasModified: boolean) => void;
    onNewSaved: (companyGroupId: string) => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
}

export interface ICompanyGroupEditDialogState {
    activeMenuItem: CompanyGroupDialogMenuItem;
    isLoading: boolean;
    companyGroupDataWasModified: boolean;
    companyGroup: ICompanyGroupEditItem;
    id: string;
    code: string;
    name: string;
    comment: string;
    authorizationLevel: number;
    rowId: string;
    companies: ICompanyListItem[];
}

export class CompanyGroupEditDialog extends React.Component<ICompanyGroupEditDialogProp, ICompanyGroupEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: ICompanyGroupEditDialogProp) {
        super(props);
        this.state = {
            activeMenuItem: Base.isNullOrUndefined(props.defaultMenuItem) ? CompanyGroupDialogMenuItem.General : props.defaultMenuItem,
            isLoading: false,
            companyGroupDataWasModified: false,
            companyGroup: null,
            id: "",
            code: "",
            name: "",
            comment: "",
            authorizationLevel: 0,
            rowId: "",
            companies: [],
        };
    }

    loadCompanyGroup = async (id: string) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyGroupService.getCompanyGroupEdit(id), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const companyGroup = result.companyGroup;
        const state = {
            ...this.state,
            companyGroup,
            id: companyGroup.id,
            code: companyGroup.code,
            name: companyGroup.name,
            comment: companyGroup.comment,
            rowId: companyGroup.rowId,
            companies: result.companies,
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadCompanyGroup(this.props.companyGroupId);
    }

    async componentDidUpdate(prevProps: ICompanyGroupEditDialogProp, prevState: ICompanyGroupEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        const prevDefaultMenuItem = Base.isNullOrUndefined(prevProps.defaultMenuItem) ? CompanyGroupDialogMenuItem.General : prevProps.defaultMenuItem;
        const defaultMenuItem = Base.isNullOrUndefined(props.defaultMenuItem) ? CompanyGroupDialogMenuItem.General : props.defaultMenuItem;
        if (prevProps.companyGroupId === props.companyGroupId) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        await this.loadCompanyGroup(props.companyGroupId);
        if (prevDefaultMenuItem !== defaultMenuItem) {
            this.setState({
                activeMenuItem: defaultMenuItem
            });
        }
    }

    handleToggleActiveMenuItem = (companyGroupDialogMenuItem: CompanyGroupDialogMenuItem) => {
        this.setState({ activeMenuItem: companyGroupDialogMenuItem });
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            this.setState({ code: value });
        } else if (name === "name") {
            this.setState({ name: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "authorizationLevel") {
            this.setState({ authorizationLevel: value.toInteger() });
        }
    };
   
    // #region Users
    handleUsersRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            companies: this.state.companies.filter(i => ids.indexOf(i.id) < 0),
            companyGroupDataWasModified: true
        });
    };
    
    handleUsersAdded = (newCompanies: ICompanyListItem[]) => {
        if (!newCompanies || newCompanies.length < 1) return;
        this.setState({
            companies: this.state.companies.concat(newCompanies),
            companyGroupDataWasModified: true
        });
    };
    // #endregion User Groups

    getSaveDataFromState = (props: ICompanyGroupEditDialogProp, state: ICompanyGroupEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        result.append("id", state.id);
        result.append("code", state.code);
        result.append("name", state.name);
        result.append("comment", state.comment);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.UserCodeMustBeDefined));
                return resolve(false);
            }
            if (!state.name) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            //validateDefaultEventHandlers
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => companyGroupService.saveCompanyGroupEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        const isNew = this.state.companyGroup.isNew();
        if (!isNew) {
            if (!closeDialog) {
                this.loadCompanyGroup(this.state.id);
                props.onSaved();
                return;
            }
            props.onClose(true);
        } else {
            props.onNewSaved(result.id);
        }
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.companyGroup.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCompanyGroupParameter, state.companyGroup.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => companyGroupService.removeCompanyGroup(state.companyGroup.id));
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(state.companyGroupDataWasModified);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!await this.saveToDb(),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.companyGroup) return null;
        const isNew = state.companyGroup.isNew();
        return (
            <Dialog
                id="user-group-edit-dialog"
                maxWidth="md"
                maxHeight={"654px"}
                title={isNew ? Translations.AddCompanyGroup : (Translations.EditCompanyGroup + ": " + state.companyGroup.code)}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: CompanyGroupDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: CompanyGroupDialogMenuItem.Companies, title: Translations.Companies }
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === CompanyGroupDialogMenuItem.General &&
                            <>
                                <GridRow>
                                    <Grid item sm={6}>
                                        <FormField title={Translations.Code} required margin={0}>
                                            <TextField type="text" name="xts" title={Translations.Code} inputProps={{ maxLength: 50 }} value={state.code} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                    <Grid item sm={6}>
                                        <FormField title={Translations.Name} required margin={0}>
                                            <TextField type="text" name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.name} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Description}>
                                            <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 500 }} multiline minRows={5} value={state.comment} required={true} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.activeMenuItem === CompanyGroupDialogMenuItem.Companies &&
                            <CompanyGroupEditDialogCompanyList
                                isReadOnly={isNew}
                                companyGroupId={state.id}
                                companies={state.companies}
                                onRemoved={this.handleUsersRemoved}
                                onAdded={this.handleUsersAdded}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            modifyUser={!isNew ? state.companyGroup.modifyUser : null}

                        />
                        <DialogButtons
                            isNew={isNew}
                            // removeDisabled={state.isLockedToOtherUser}
                            // saveDisabled={state.isLockedToOtherUser}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}